import { colors, shadows } from '../designTokens';
import { Component, ErrorInfo, ReactNode } from 'react';
import i18n from '../i18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary Component - Catches React rendering errors in child components.
 * 
 * Features:
 * - Catches JavaScript errors anywhere in child component tree
 * - Logs error details to console (and optionally to backend)
 * - Displays fallback UI instead of crashing the entire app
 * - Provides reset functionality to recover from errors
 * - Shows detailed error information in development mode
 * 
 * Usage:
 * ```tsx
 * <ErrorBoundary fallback={<ErrorFallback />}>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 * 
 * Note: Error Boundaries only catch errors in:
 * - Rendering
 * - Lifecycle methods
 * - Constructors of the whole tree below them
 * 
 * They do NOT catch errors in:
 * - Event handlers (use try-catch)
 * - Asynchronous code (setTimeout, promises)
 * - Server side rendering
 * - Errors thrown in the error boundary itself
 * 
 * @author People Rock Development Team
 * @version 1.0.0 (Phase 5)
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state when an error is caught.
   * This is called during the "render" phase, so side effects are not allowed.
   */
  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error details.
   * This is called during the "commit" phase, so side effects are allowed.
   */
  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error info
    this.setState({
      errorInfo,
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // TODO: Send error to monitoring service (e.g., Sentry)
    // logErrorToService(error, errorInfo);
  }

  /**
   * Reset error boundary state to recover from error.
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  override render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <div style={styles.container}>
          <div style={styles.content}>
            <h1 style={styles.title}>{i18n.t('errorBoundary.title', { ns: 'common' })}</h1>
            <p style={styles.message}>
              {i18n.t('errorBoundary.message', { ns: 'common' })}
            </p>

            <button onClick={this.handleReset} style={styles.button}>
              {i18n.t('errorBoundary.tryAgain', { ns: 'common' })}
            </button>

            {/* Show error details in development mode */}
            {import.meta.env.DEV && error && (
              <details style={styles.details}>
                <summary style={styles.summary}>Error Details (Development Only)</summary>
                <div style={styles.errorDetails}>
                  <h3 style={styles.errorTitle}>Error Message:</h3>
                  <pre style={styles.pre}>{error.toString()}</pre>

                  {errorInfo && (
                    <>
                      <h3 style={styles.errorTitle}>Component Stack:</h3>
                      <pre style={styles.pre}>{errorInfo.componentStack}</pre>
                    </>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return children;
  }
}

// Inline styles for fallback UI
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: colors.superficie,
    padding: '20px',
  },
  content: {
    maxWidth: '600px',
    width: '100%',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '40px',
    boxShadow: shadows.suave,
    textAlign: 'center' as const,
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold' as const,
    color: '#b3261e',
    marginBottom: '16px',
  },
  message: {
    fontSize: '16px',
    color: colors.tintaSuave,
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  button: {
    backgroundColor: colors.acento,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 'bold' as const,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  details: {
    marginTop: '32px',
    textAlign: 'left' as const,
    backgroundColor: colors.superficieElevada,
    borderRadius: '4px',
    padding: '16px',
  },
  summary: {
    cursor: 'pointer',
    fontWeight: 'bold' as const,
    marginBottom: '16px',
    color: colors.acentoTexto,
  },
  errorDetails: {
    marginTop: '16px',
  },
  errorTitle: {
    fontSize: '14px',
    fontWeight: 'bold' as const,
    color: colors.tinta,
    marginTop: '16px',
    marginBottom: '8px',
  },
  pre: {
    backgroundColor: colors.superficieElevada,
    border: `1px solid ${colors.linha}`,
    borderRadius: '4px',
    padding: '12px',
    fontSize: '12px',
    overflow: 'auto',
    maxHeight: '200px',
    fontFamily: 'monospace',
  },
};

export default ErrorBoundary;
